import {
  DocumentReference,
  DocumentSnapshot,
  FirestoreError,
} from "@atgof-firebase/types";
import {
  Colour,
  DarkCompatibleColour,
  MembershipCategory,
  normaliseColour,
} from "./model/project/membership";
import { OnNext, onSnapshot, onSnapshots } from "./onSnapshot";
import { notDeleted } from "./general";

export type MembershipResults =
  | null
  | { error: FirestoreError }
  | { docs: DocumentSnapshot[] };

export type UserInkColour = {
  displayName: string;
  userId: string;
  inkColour: DarkCompatibleColour;
};

function userInkColourForMembership(
  doc: DocumentSnapshot,
): UserInkColour | undefined {
  const inkColour: Colour | undefined = doc.get("inkColour");
  if (!inkColour) return;
  const user: DocumentReference = doc.get("user");
  return {
    displayName: doc.get("displayName"),
    userId: user.id,
    inkColour: normaliseColour(inkColour),
  };
}

export function onUserName(
  project: DocumentReference | undefined,
  userRef: DocumentReference | undefined,
  onNext: OnNext<string | undefined>,
) {
  if (!(project && userRef)) {
    onNext(undefined);
    return;
  }
  return onSnapshot<DocumentSnapshot, string>(
    (onNext) =>
      project?.collection("memberships").doc(userRef.id).onSnapshot(onNext),
    (doc) => (doc.get("deleted") ? undefined : doc.get("displayName")),
    onNext,
  );
}

export function onUserInkColours(
  project: DocumentReference | undefined,
  categories: readonly MembershipCategory[],
  onNext: OnNext<UserInkColour[] | undefined>,
) {
  if (!categories.length) {
    onNext([]);
    return;
  }
  if (!project) {
    onNext(undefined);
    return;
  }
  return onSnapshots<MembershipCategory, UserInkColour[]>(
    categories,
    (category) => {
      return (onNext) =>
        notDeleted(
          project
            .collection("memberships")
            .where("roles." + category + ".user", "==", true),
        ).onSnapshot(({ docs }) =>
          onNext(
            docs
              .map(userInkColourForMembership)
              .filter((x) => x) as UserInkColour[],
          ),
        );
    },
    (entries) =>
      onNext(
        entries
          ?.flat()
          .reduce(
            (acc, entry) =>
              acc.find(({ userId }) => userId === entry.userId)
                ? acc
                : [...acc, entry],
            [] as UserInkColour[],
          )
          .sort((a, b) => a.displayName.localeCompare(b.displayName)),
      ),
  );
}
